import { fetchWrapper } from '@/lib/fetch-wrapper';
import { urls } from '@/urls';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  styled,
  Typography,
} from '@mui/material';
import { useEffect, useState, type CSSProperties } from 'react';
import { Loader } from '../loader/loader';
import {
  formatContextValue,
  getContextNumericValue,
} from '@/lib/formatters';
import RecommendationModal from '../../screens/optimization/components/recommendation-modal';
import { ActionCenterDetails } from '@cloudcost/shared-types/action-center';
import {
  getContextDisplayLabel,
  isContextDisplaySectionBreak,
  isHiddenContextField,
} from '@cloudcost/shared-types/dispute-context-display';
import { Pill } from '@/components/ds';
import { T, statusTone } from '@/theme/tokens';

const kvLabelStyle: CSSProperties = {
  color: T.textFaint,
  fontWeight: 700,
  fontSize: '0.8125rem',
};
const kvValueStyle: CSSProperties = {
  color: T.text,
  fontWeight: 700,
  fontSize: '0.8125rem',
  textAlign: 'right',
};

interface DisputeDetailsModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  resource: any;
}

const getIdentifierByActionType = (resource: any): string => {
  if (!resource?.action_type) return '';

  switch (resource.action_type.toLowerCase()) {
    case 'attestation':
      return resource.responsible_id;
    case 'budget':
      return resource.id_budget;
    case 'allocation':
      return resource.responsible_id;
    case 'recommendation':
      return resource.id_recommendation;
    default:
      return '';
  }
};

const DisputeDetailsModal = ({
  open,
  setOpen,
  resource,
}: DisputeDetailsModalProps) => {
  const [details, setDetails] = useState<ActionCenterDetails>(
    {} as ActionCenterDetails,
  );
  const [loading, setLoading] = useState(false);
  const [recommendationModalOpen, setRecommendationModalOpen] = useState(false);
  const [recommendationData, setRecommendationData] = useState<any>(null);

  useEffect(() => {
    const identifier = getIdentifierByActionType(resource);
    if (identifier && resource?.action_type) {
      setLoading(true);
      fetchWrapper
        .get(
          `${
            urls.DevOps.actionCenterDetails
          }/${resource?.action_type?.toLowerCase()}/${identifier}`,
        )
        .then(setDetails)
        .finally(() => {
          setLoading(false);
        });
    } else {
      setDetails({} as ActionCenterDetails);
    }
  }, [resource]);

  const handleOpenRecommendationModal = async () => {
    if (resource?.action_type?.toLowerCase() === 'recommendation') {
      try {
        setLoading(true);
        // Fetch complete recommendation data from recommendations table
        const fullRecommendationData = await fetchWrapper.get(
          `${urls.DevOps.getRecommendationDetails}/${resource.id_recommendation}`,
        );
        setRecommendationData(fullRecommendationData);
        setRecommendationModalOpen(true);
      } catch (error) {
        console.error('Error fetching recommendation details:', error);
        // Fallback to using the resource data if the fetch fails
        setRecommendationData(resource);
        setRecommendationModalOpen(true);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleCloseRecommendationModal = () => {
    setRecommendationModalOpen(false);
    setRecommendationData(null);
  };

  const isRecommendationDispute =
    resource?.action_type?.toLowerCase() === 'recommendation';

  return (
    <>
      <StyledDialog open={open} maxWidth="lg">
        {loading && <Loader style={{ maxHeight: 200, minHeight: 200 }} />}

        {!loading && !details.title && (
          <DialogContent>
            <Alert severity="error">No data.</Alert>
          </DialogContent>
        )}

        {!loading && details.title && (
          <>
            <DialogTitle sx={{ pb: 1 }}>
              <Typography
                sx={{ fontWeight: 900, fontSize: '1.15rem', color: T.text, letterSpacing: '-.2px' }}>
                {details.title}
              </Typography>
            </DialogTitle>

            <DialogContent>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="normal"
                flexDirection="column"
                gap={0.75}
                mb={2}
                mt={2}>
                {Object.entries(details.details)
                  .filter(([key, value]: any) => {
                    if (isHiddenContextField(key)) return false;
                    const num = getContextNumericValue(value);
                    return num === null || num !== 0;
                  })
                  .map(([key, value]: any) => {
                    if (isContextDisplaySectionBreak(key)) {
                      return <Box key={key} sx={{ height: 12 }} aria-hidden />;
                    }
                    const label = getContextDisplayLabel(key);
                    const isStatus = /status/i.test(key);
                    const isColumn = label === 'Recommendation type' || key === 'Recommendation type';
                    return (
                      <Box
                        key={key}
                        display="flex"
                        alignItems={isColumn ? 'flex-start' : 'center'}
                        flexDirection={isColumn ? 'column' : 'row'}
                        gap={isColumn ? 0 : 2}
                        justifyContent={'space-between'}>
                        <span style={kvLabelStyle}>{label}</span>
                        {isStatus ? (
                          <Pill tone={statusTone(String(value))}>
                            {formatContextValue(value)}
                          </Pill>
                        ) : (
                          <span
                            style={
                              isColumn ? { ...kvValueStyle, textAlign: 'left' } : kvValueStyle
                            }>
                            {formatContextValue(value)}
                          </span>
                        )}
                        {isColumn && <br />}
                      </Box>
                    );
                  })}
              </Box>
            </DialogContent>
          </>
        )}

        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1 }}>
          {isRecommendationDispute && (
            <Button
              variant="contained"
              disableElevation
              onClick={handleOpenRecommendationModal}
              sx={{
                mr: 1,
                textTransform: 'none',
                fontWeight: 800,
                borderRadius: `${T.radiusSm}px`,
                px: 2.25,
                backgroundColor: T.primary,
                '&:hover': { backgroundColor: T.primaryHover },
              }}>
              View Recommendation Details
            </Button>
          )}
          <Button
            onClick={() => setOpen(false)}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              color: T.textFaint,
              borderRadius: `${T.radiusSm}px`,
            }}>
            Close
          </Button>
        </DialogActions>
      </StyledDialog>

      <RecommendationModal
        recommendation={recommendationData}
        onClose={handleCloseRecommendationModal}
      />
    </>
  );
};

const StyledDialog = styled(Dialog)(({ theme }) => ({
  zIndex: 999999999,
  '& .MuiDialog-paper': {
    width: 500,
  },
}));

export default DisputeDetailsModal;
